// popupManager.js
export default class PopupManager {
  constructor(popups, currentNodeId = null) {
    this.popups = popups;
    this.currentNodeId = currentNodeId;
    // track popups that are currently loading or already shown
    this._loading = new Set();
    this._shown = new Set();
    this._timers = new Set();
    this._listeners = [];
  }

  parseSettings(popup) {
    try {
      return typeof popup.popup_settings === 'string'
        ? JSON.parse(popup.popup_settings || '{}')
        : (popup.popup_settings || {});
    } catch (error) {
      console.warn(`Invalid popup settings for ${popup.noahs_id}`, error);
      return {};
    }
  }

  /**
   * Decide si un popup debe mostrarse según condiciones.
   */
  shouldShowPopup(popup) {
    const popupSetting = this.parseSettings(popup);
    const conditions = popupSetting.conditions || [];
    let hasInclude = false;
    let matchedInclude = false;
    let matchedExclude = false;

    for (const condition of Object.values(conditions)) {
      const { action, type } = condition;
      let match = false;

      if (type === 'entire_site') match = true;

      if (type === 'single_page' && Array.isArray(condition.pages)) {
        match = condition.pages.map(String).includes(String(this.currentNodeId));
      }

      if (type === 'content_type' && Array.isArray(condition.types)) {
        const contentType = drupalSettings.noahs_popups?.entity?.bundle || drupalSettings.node?.type;
        match = Boolean(contentType && condition.types.includes(contentType));
      }

      if (type === 'entity_type' && Array.isArray(condition.entities)) {
        const entity = drupalSettings.noahs_popups?.entity;
        match = Boolean(entity && condition.entities.includes(`${entity.type}:${entity.id}`));
      }
      if (type === 'custom_url') {
        const path = String(condition.url || '').trim();
        // Only site-relative paths; query strings and fragments do not affect targeting.
        if (/^\/(?!\/)/.test(path) && !/[?#\\]/.test(path)) {
          const normalize = value => value.replace(/\/+$/, '') || '/';
          match = normalize(window.location.pathname) === normalize(path);
          const systemPath = drupalSettings.path?.currentPath;
          if (systemPath) match = match || normalize('/' + systemPath) === normalize(path);
        }
      }

      if (action === 'include') {
        hasInclude = true;
        if (match) matchedInclude = true;
      }
      if (action === 'exclude' && match) matchedExclude = true;
    }

    let show = hasInclude && matchedInclude && !matchedExclude;

    if (!this.canShowPopup(popup)) show = false;
    const eligibility = { popup, allowed: show, manager: this };
    document.dispatchEvent(new CustomEvent('noahs:popup-eligibility', { detail: eligibility }));
    show = eligibility.allowed;

    return show;
  }

  /**
   * Calcula tiempo de expiración en milisegundos.
   */
  calculateCloseDelay(close_action) {
    if (!close_action) return 7 * 24 * 60 * 60 * 1000;

    const { delay_value, delay_unit } = close_action;
    const parsedValue = parseInt(delay_value, 10);
    const value = Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 7;

    switch (delay_unit) {
      case 'seconds': return value * 1000;
      case 'minutes': return value * 60 * 1000;
      case 'hours':   return value * 60 * 60 * 1000;
      case 'days':    return value * 24 * 60 * 60 * 1000;
      default:        return 7 * 24 * 60 * 60 * 1000;
    }
  }

  /**
   * Comprueba si el popup puede mostrarse según localStorage.
   */
  canShowPopup(popup) {
    const closedUntil = localStorage.getItem(`popupClosedUntil_${popup.noahs_id}`);
    if (closedUntil && Date.now() < parseInt(closedUntil, 10)) return false;
    return true;
  }

  /**
   * Marca popup como cerrado en localStorage.
   */
  registerPopupClosed(popup) {
    const popupSetting = this.parseSettings(popup);
    const delay = this.calculateCloseDelay(popupSetting.close_action);
    const expireAt = Date.now() + delay;
    localStorage.setItem(`popupClosedUntil_${popup.noahs_id}`, expireAt);
  }

  /**
   * Carga el HTML del popup y lo agrega al DOM.
   */
  async loadPopup(popup) {
    // Prevent duplicate loads: if already present in DOM or loading, skip
    const existing = document.querySelector(`#noahs-popup-${popup.noahs_id}`);
    if (existing) return;
    if (this._loading.has(popup.noahs_id) || this._shown.has(popup.noahs_id)) return;

    this._loading.add(popup.noahs_id);

    const loader = document.createElement('div');
    try {
      loader.classList.add('noahs-popup-loader');
      loader.innerHTML = `<div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>`;
      document.body.appendChild(loader);

      const response = await fetch(`/noahs-popup/render/${popup.noahs_id}`);
      if (!response.ok) {
        this._loading.delete(popup.noahs_id);
        loader.remove();
        return;
      }

      const html = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      const popupElement = wrapper.firstElementChild;
      if (!popupElement) {
        this._loading.delete(popup.noahs_id);
        return;
      }

  // Id consistente para poder cerrar desde botones
  popupElement.id = `noahs-popup-${popup.noahs_id}`;

      // Evento de cerrar
      popupElement.querySelector('.noahs_close_popup')?.addEventListener('click', () => this.closePopup(popup, popupElement, 'close_button'));

      loader.remove();
      document.body.appendChild(popupElement);
      // mark as shown and clear loading
      this._shown.add(popup.noahs_id);
      this._loading.delete(popup.noahs_id);
      document.dispatchEvent(new CustomEvent('noahs:popup-opened', { detail: { popup, element: popupElement, manager: this } }));
    } catch (err) {
      this._loading.delete(popup.noahs_id);
      console.error(`Error al cargar popup ${popup.noahs_id}:`, err);
    } finally {
      loader.remove();
      this._loading.delete(popup.noahs_id);
    }
  }

  closePopup(popup, popupElement = null, reason = 'manual') {
    const element = popupElement || document.querySelector(`#noahs-popup-${popup.noahs_id}`);
    if (!element) return;

    const finish = () => {
      element.remove();
      this.registerPopupClosed(popup);
      this._shown.delete(popup.noahs_id);
      document.dispatchEvent(new CustomEvent('noahs:popup-closed', { detail: { popup, reason, manager: this } }));
    };
    const exitClass = element.dataset.noahsExitClass;
    const exitDuration = parseInt(element.dataset.noahsExitDuration || 0, 10);
    if (exitClass && exitDuration > 0) {
      element.classList.add(exitClass);
      window.setTimeout(finish, exitDuration);
    } else {
      finish();
    }
  }

  /**
   * Ejecuta triggers automáticos de los popups.
   */
  runTriggers() {
    for (const popup of Object.values(this.popups)) {
      if (!this.shouldShowPopup(popup)) continue;

      const popupSetting = this.parseSettings(popup);
      const triggers = popupSetting.triggers || {};

      // On Load
      if ([true, 'true', 1, '1'].includes(triggers.on_load?.enabled)) {
        const delay = parseInt(triggers.on_load.delay || 0, 10) * 1000;
        const timer = window.setTimeout(() => {
          this._timers.delete(timer);
          this.loadPopup(popup);
        }, delay);
        this._timers.add(timer);
      }

      // On Scroll
      if ([true, 'true', 1, '1'].includes(triggers.on_scroll?.enabled)) {
        const offset = parseInt(triggers.on_scroll.offset || 0, 10);
        const handler = (event) => {
          const target = event?.target;
          const scrollTop = Math.max(window.scrollY || 0, document.scrollingElement?.scrollTop || 0, target?.scrollTop || 0);
          if (scrollTop >= offset) {
            // prevent duplicate scroll-triggered loads
            if (!this._loading.has(popup.noahs_id) && !this._shown.has(popup.noahs_id)) {
              this.loadPopup(popup);
            }
            document.removeEventListener('scroll', handler, true);
          }
        };
        document.addEventListener('scroll', handler, { capture: true, passive: true });
        this._listeners.push(['scroll', handler]);
        handler();
      }
    }
  }

  /**
   * Maneja los botones que abren o cierran popups manualmente.
   */
  runButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-popup-action]');

      if (!btn) return;

      e.preventDefault();

      const action = btn.dataset.popupAction; // "open_popup" | "close_popup"
      const popupId = btn.dataset.popupSettings;
      console.log(`Button clicked: ${action}, Popup ID: ${popupId}`);
      if (!popupId) return;

      const popup = this.popups[popupId];
      if (!popup) return;

      if (action === 'open_popup') {
        this.loadPopup(popup); // ignorando triggers y condiciones
      }

      if (action === 'close_popup') {
        const el = document.querySelector(`#noahs-popup-${popupId}`);
        if (el) this.closePopup(popup, el, 'action_button');
      }
    });
  }

  /**
   * Inicializa todo: triggers y botones manuales.
   */
  init() {
    this.runTriggers();
    this.runButtons();
    document.dispatchEvent(new CustomEvent('noahs:popups-ready', { detail: { manager: this } }));
  }
}
